import { DisplayValue, IHashMap, isNullOrUndefined } from '@adaptiva/core/common';
import { MenuItem } from '@adaptiva/core/menus';
import { OverlayService } from '@adaptiva/core/overlays';
import { IRowEvent, IRowMovedEvent, SchemaAndRows } from '@adaptiva/core/tables';
import { UiService } from '@adaptiva/core/ui';
import { BaseWidgetComponent, WidgetService } from '@adaptiva/core/widgets';
import { DataSetColumn, EJavaClassNames } from '@adaptiva/data-service';
import { ISensorActionSequence, ISensorActionStep, MetadataObject, SensorActionStep } from '@adaptiva/metadata/common';
import { ExpressionLanguageEditorComponent } from '@adaptiva/widgets/expression-editor';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { TableComponent } from '@adaptiva/widgets/table';
import { ChangeDetectionStrategy, Component, EventEmitter, forwardRef, input, Input, OnInit, Output, Signal, ViewChild } from '@angular/core';
import { cloneDeep, isEqual } from 'lodash';
import { BehaviorSubject, of } from 'rxjs';
import { switchMap, take } from 'rxjs/operators';
import { IReturnCode } from './interfaces';

@Component({
	standalone: false,
	selector: 'ada-sensor-action-sequence',
	templateUrl: './sensor-action-sequence.component.html',
	styleUrls: ['./sensor-action-sequence.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		WidgetService,
		{
			provide: BaseWidgetComponent,
			useExisting: forwardRef(() => SensorActionSequenceComponent)
		}
	]
})
export class SensorActionSequenceComponent extends BaseWidgetComponent implements OnInit {

	@Input()
	public set sequence(_sequence: ISensorActionSequence) {
		if (_sequence)
			this._sequence = cloneDeep(_sequence);
		else
			this._setDefaultSequence();

		this._setActionStepData(this._sequence);
	}

	@Input()
	public availableProperties: BehaviorSubject<string[]>;

	@Input()
	public propertyName: string;

	@Input()
	public metadataObject: MetadataObject;

	@Input()
	public parentSequence!: ISensorActionSequence;

	protected readonly contextClassName: Signal<EJavaClassNames> = input(null);

	@Output()
	public sequenceChange: EventEmitter<ISensorActionSequence> = new EventEmitter();

	@ViewChild(TableComponent)
	public tableComponent: TableComponent;

	@ViewChild('ExpressionEditor')
	protected expressionEditor: ExpressionLanguageEditorComponent;

	@ViewChild('ActionStepOverlay')
	private _actionStepOverlay: OverlayComponent;

	// Table Data Subjects

	public actionStepData$: BehaviorSubject<SchemaAndRows<ISensorActionStep>> = new BehaviorSubject(null);

	public returnCodeData$: BehaviorSubject<SchemaAndRows<IReturnCode>> = new BehaviorSubject(null);

	// Table Column Names

	public returnCodeColumnNames: string[] = [
		'Return Code',
		'Behavior'
	];

	public behaviorOptions: DisplayValue[] = [
		new DisplayValue('Success', SensorActionStep.Behavior.SUCCESS),
		new DisplayValue('Success with Reboot Pending', SensorActionStep.Behavior.SUCCESS_REBOOT_PENDING),
		new DisplayValue('Failure', SensorActionStep.Behavior.FAILURE)
	];

	// Overlay Properties

	public overlayStep: ISensorActionStep;

	public overlayReturnCodes: IReturnCode[];

	public editingIndex: int = -1;

	public stepError$: BehaviorSubject<string> = new BehaviorSubject('');

	public returnCodeError$: BehaviorSubject<string> = new BehaviorSubject('');

	protected readonly onDisableRemoveStep: BehaviorSubject<boolean> = new BehaviorSubject(this.isDisabled);

	protected readonly sensorActionsTableRowMenus: MenuItem<IRowEvent<ISensorActionStep>>[] = [
		new MenuItem({
			name: 'Edit Sensor Action Step',
			onSelect: (_menu: MenuItem<IRowEvent<ISensorActionStep>>) => this.openActionStepOverlay(_menu.data)
		}),
		new MenuItem({
			name: 'Remove Sensor Action Step',
			isDisabled: this.onDisableRemoveStep,
			onSelect: (_menu: MenuItem<IRowEvent<ISensorActionStep>>) => this.removeActionStep(_menu.data)
		})
	];

	protected readonly returnCodeTableRowMenus: MenuItem<IRowEvent<IReturnCode>>[] = [
		new MenuItem({
			name: 'Remove Return Code',
			onSelect: (_menu: MenuItem<IRowEvent<IReturnCode>>) => this.removeReturnCode(_menu.data)
		}),
	];

	private _sequence: ISensorActionSequence;

	public get sequence(): ISensorActionSequence {
		return this._sequence;
	}

	constructor(
		public UI: UiService,
		public widgetService: WidgetService,
		public overlayService: OverlayService
	) {
		super(widgetService);
	}

	public override ngOnInit(): void {
		super.ngOnInit();
		if (!this.sequence)
			this._setDefaultSequence();
	}

	// Action Step Table Methods

	public openActionStepOverlay(_event?: IRowEvent<ISensorActionStep>): void {
		this.stepError$.next('');
		this.returnCodeError$.next('');
		const _step: ISensorActionStep = _event?.row;
		this.editingIndex = _event?.rowIndex > -1 ? _event.rowIndex : -1;
		let _returnCodes: IReturnCode[];
		if (_step) {
			this.overlayStep = cloneDeep(_step);
			if (_step.returnCodesToBehaviors) {
				_returnCodes = Object.keys(_step.returnCodesToBehaviors).map(_code => {
					return {
						returnCode: +_code,
						behavior: _step.returnCodesToBehaviors[_code]
					};
				});
			}
		} else
			this.overlayStep = new SensorActionStep();

		this.overlayReturnCodes = _returnCodes || [{
			returnCode: undefined,
			behavior: SensorActionStep.Behavior.FAILURE
		}];
		this._setReturnCodeData();
		this.detectChanges();
		if (!this._actionStepOverlay?.isOpen)
			this._actionStepOverlay?.open();
	}

	public addActionStep(_confirmed: boolean): void {
		if (!_confirmed || this.isDisabled)
			return;

		this.overlayStep.returnCodesToBehaviors = this.overlayReturnCodes.reduce<IHashMap<int>>((_map, _item) => {
			_map[_item.returnCode] = +_item.behavior;
			return _map;
		}, {});

		if (this.editingIndex > -1) {
			this.sequence.steps = [...this.sequence.steps];
			this.sequence.steps[this.editingIndex] = this.overlayStep;
		} else
			this.sequence.steps.push(this.overlayStep);

		this.sequenceChange.emit(this.sequence);
		this._setActionStepData(this.sequence);
	}

	public removeActionStep(_event: IRowEvent<ISensorActionStep>): void {
		if (_event?.rowIndex > -1) {
			this.overlayService.confirm(
				'Are you sure?',
				'Removing an action sequence step cannot be undone.'
			).pipe(
				take(1)
			).subscribe({
				next: (_confirmed: boolean) => {
					if (!_confirmed)
						return;

					const _index: int = _event.rowIndex + this.tableComponent.offset();

					this.tableComponent?.removeRow(_event);
					this.sequence.steps.splice(_index, 1);

					if (this.sequence.steps.length === 0) {
						this.sequenceChange.emit(null);
						this.sequence = this.parentSequence;
					} else
						this.sequenceChange.emit(this.sequence);
				}
			});
		}
	}

	public onRowMoved(_event: IRowMovedEvent<ISensorActionStep>): void {
		this.sequence.steps = _event.allRows;
		this._setActionStepData(this.sequence);
		this.sequenceChange.emit(this.sequence);
	}

	public validateStep(): boolean {
		let _returnCodesValidated: boolean = true;
		if (this.overlayReturnCodes?.length) {
			// Remove Empty Steps
			this.overlayReturnCodes = this.overlayReturnCodes.filter(_code => !isNaN(_code.returnCode));
			if (_returnCodesValidated) {
				const _hasInvalidProps: boolean = !!this.overlayReturnCodes.find(_code => {
					return _code.returnCode !== 0 && _code.returnCode % 1 !== 0;
				});
				this.returnCodeError$.next(_hasInvalidProps ? 'Return Codes must be Whole Numbers' : '');
				_returnCodesValidated = !_hasInvalidProps;
			}
		}
		return _returnCodesValidated;
	}

	// Inter-step navigation: 

	public loadNextStep(): void {
		if (this.editingIndex === this.sequence.steps.length - 1)
			return;

		this._loadStep(this.editingIndex + 1);
	}

	public loadPreviousStep(): void {
		if (this.editingIndex <= 0)
			return;

		this._loadStep(this.editingIndex - 1);
	}

	private _loadStep(_index: int): void {
		const isValid: boolean = this.validateStep();
		if (!isValid)
			return;

		of(isEqual(this.overlayStep, this.sequence.steps[this.editingIndex])).pipe(
			switchMap((_noChanges: boolean) => {
				return _noChanges ? of(false) : this.overlayService.confirm(
					'Save changes?',
					'Would you like to save the changes to the Sensor Action Step before navigating?',
					null,
					{
						cancelButtonText: 'No',
						confirmButtonText: 'Yes'
					}
				);
			})
		).subscribe({
			next: (_confirmed: boolean) => {
				this.addActionStep(_confirmed);
				const _nextStep: SensorActionStep = this.sequence.steps[_index];
				this.expressionEditor.setValue(_nextStep.sensorActionExpression);
				this.openActionStepOverlay({
					rowIndex: _index,
					row: _nextStep
				});
			}
		});
	}


	// Return Code Table Methods

	public addReturnCode(): void {
		this.overlayReturnCodes.push({
			returnCode: undefined,
			behavior: SensorActionStep.Behavior.FAILURE
		});
		this._setReturnCodeData();
	}

	public removeReturnCode(_event: IRowEvent<IReturnCode>): void {
		if (_event?.rowIndex > -1) {
			this.overlayReturnCodes.splice(_event.rowIndex, 1);
			this._setReturnCodeData();
		}
	}

	public setPrimaryStep(_step: int): void {
		if (isNullOrUndefined(_step))
			_step = 0;

		this.sequence.primaryStep = Math.max(_step, 0);

		if (this.sequence.steps.length)
			this.sequenceChange.emit(this.sequence);
	}

	protected disableSensorActionsRowMenus(_isOpen: boolean): void {
		if (_isOpen)
			this.onDisableRemoveStep.next(this.isDisabled);
	}

	private _setActionStepData(_sequence: ISensorActionSequence): void {
		const _data: SchemaAndRows<ISensorActionStep> = new SchemaAndRows(
			[
				new DataSetColumn({ columnName: 'sensorActionExpression', columnDisplay: 'Sensor Expression', type: 'string' }),
			],
			[..._sequence.steps],
			_sequence.steps?.length
		);

		this.actionStepData$.next(_data);
		this._cd.detectChanges();
	}

	private _setReturnCodeData(): void {
		const _data: SchemaAndRows<IReturnCode> = new SchemaAndRows(
			[
				new DataSetColumn({ columnName: 'returnCode', columnDisplay: 'Return Code', type: 'number' }),
				new DataSetColumn({ columnName: 'behavior', columnDisplay: 'Behavior', type: 'number' }),
			],
			[...this.overlayReturnCodes],
			this.overlayReturnCodes.length
		);

		this.returnCodeData$.next(_data);
	}

	private _setDefaultSequence(): void {
		this._sequence = {
			steps: [],
			primaryStep: 0
		};
	}

}
